import React, {useEffect, useRef, useState} from 'react';
import {
  Animated,
  Easing,
  Platform,
  Pressable,
  ScrollView,
  TextInput,
  View,
} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {S, STATUS, T} from '../courseTheme';
import {E, MESSAGE_COUNTER_FROM, MESSAGE_LIMIT} from './editorTheme';
import {
  ChatMessage,
  chatErrorText,
  Revision,
  shouldSendOnKey,
} from './editorLogic';
import {IconButton, Notice, ring, visuallyHidden, focusVisible} from './ui';

export type SendHandlers = {onError: (e: unknown) => void};

type Props = {
  /** "Chapter 7" — what this thread is about. */
  chapterLabel: string;
  messages: ChatMessage[];
  loading: boolean;
  sending: boolean;
  suggestions: string[];
  /** The page's revisions, newest first: says which batch can be undone. */
  history: Revision[] | undefined;
  latestUndoable: string | null;
  onSend: (message: string, handlers: SendHandlers) => void;
  onUndo: (batchId: string, labels: string[]) => void;
  reduced: boolean;
};

/** Three dots that breathe while the assistant works; still under reduced motion. */
function Thinking({reduced}: {reduced: boolean}) {
  const pulse = useRef(new Animated.Value(0.35)).current;
  useEffect(() => {
    if (reduced) return undefined;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 600,
          easing: Easing.out(Easing.quad),
          useNativeDriver: false,
        }),
        Animated.timing(pulse, {
          toValue: 0.35,
          duration: 600,
          easing: Easing.in(Easing.quad),
          useNativeDriver: false,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [reduced, pulse]);
  return (
    <View
      accessible
      accessibilityLabel="The assistant is working on it"
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: S[2],
        alignSelf: 'flex-start',
        paddingVertical: S[2],
        paddingHorizontal: S[3],
        borderRadius: 14,
        borderWidth: 1,
        borderColor: E.line,
        backgroundColor: E.surface,
      }}>
      <Animated.View
        style={{flexDirection: 'row', gap: 4, opacity: reduced ? 0.7 : pulse}}>
        {[0, 1, 2].map(i => (
          <View
            key={i}
            style={{
              width: 6,
              height: 6,
              borderRadius: 3,
              backgroundColor: E.accent,
            }}
          />
        ))}
      </Animated.View>
      <CCText style={{fontSize: T.sm, color: E.muted}}>Thinking…</CCText>
    </View>
  );
}

function Chip({label, muted}: {label: string; muted?: boolean}) {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        maxWidth: '100%',
        paddingHorizontal: S[2] + 2,
        paddingVertical: 5,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: E.line,
        backgroundColor: E.canvas,
      }}>
      <Ionicons
        name={muted ? 'return-up-back' : 'checkmark'}
        size={13}
        color={muted ? E.muted : STATUS.good.fg}
      />
      <CCText
        style={{
          flexShrink: 1,
          fontSize: T.xs + 1,
          color: muted ? E.muted : E.ink,
          lineHeight: 17,
        }}>
        {label}
      </CCText>
    </View>
  );
}

function AssistantMessage({
  m,
  undoable,
  undone,
  onUndo,
}: {
  m: ChatMessage;
  undoable: boolean;
  undone: boolean;
  onUndo: () => void;
}) {
  const labels = m.labels ?? [];
  return (
    <View style={{alignSelf: 'stretch', gap: S[2], paddingRight: S[6]}}>
      <CCText
        selectable
        style={{fontSize: T.base, color: E.ink, lineHeight: 22}}>
        {m.text}
      </CCText>
      {labels.length ? (
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 6,
          }}>
          {labels.map((l, i) => (
            // "Undone: …" chips record an undo the chat made; there is no undo of an undo.
            <Chip
              key={`${l}-${i}`}
              label={l}
              muted={undone || /^Undone:/.test(l)}
            />
          ))}
          {m.batch_id ? (
            undone ? (
              <CCText
                style={{
                  fontSize: T.xs,
                  color: E.muted,
                  paddingHorizontal: S[1],
                }}>
                Undone
              </CCText>
            ) : (
              <UndoButton
                labels={labels}
                disabled={!undoable}
                onPress={onUndo}
              />
            )
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

function UndoButton({
  labels,
  disabled,
  onPress,
}: {
  labels: string[];
  disabled: boolean;
  onPress: () => void;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`Undo ${labels.join(', ')}`}
      accessibilityHint={
        disabled ? 'Only the latest change can be undone' : undefined
      }
      accessibilityState={{disabled}}
      disabled={disabled}
      onPress={() => {
        if (!disabled) onPress();
      }}
      onFocus={() => setFocused(focusVisible())}
      onBlur={() => setFocused(false)}
      style={({pressed}) => [
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
          minHeight: 44,
          minWidth: 44,
          paddingHorizontal: S[2],
          borderRadius: 10,
          backgroundColor: pressed ? E.pressed : 'transparent',
          opacity: disabled ? 0.45 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        } as any,
        ring(focused),
      ]}>
      <Ionicons name="arrow-undo" size={16} color={E.accent} />
      <CCText style={{fontSize: T.sm, fontWeight: '600', color: E.accent}}>
        Undo
      </CCText>
    </Pressable>
  );
}

function Suggestion({text, onPress}: {text: string; onPress: () => void}) {
  const [focused, setFocused] = useState(false);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={text}
      onPress={onPress}
      onFocus={() => setFocused(focusVisible())}
      onBlur={() => setFocused(false)}
      style={({pressed, hovered}: any) => [
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: S[2],
          minHeight: 44,
          paddingHorizontal: S[3],
          paddingVertical: S[2],
          borderRadius: 12,
          borderWidth: 1,
          borderColor: 'rgba(38,35,34,0.16)',
          backgroundColor: pressed ? E.pressed : hovered ? E.hover : E.surface,
          cursor: 'pointer',
        } as any,
        ring(focused),
      ]}>
      <Ionicons name="chatbubble-ellipses-outline" size={16} color={E.accent} />
      <CCText
        style={{flex: 1, fontSize: T.sm + 1, color: E.ink, lineHeight: 20}}>
        {text}
      </CCText>
    </Pressable>
  );
}

/**
 * The chapter's thread with the assistant, and the box to type in. This is
 * the editor's main control: the reviewer says what is wrong, the change is
 * applied at once, and the chips under the reply say exactly what changed —
 * built by the server from what it applied, never from the model's claims.
 *
 * Only the thread scrolls; the input stays pinned under it.
 */
export default function ChatPanel({
  chapterLabel,
  messages,
  loading,
  sending,
  suggestions,
  history,
  latestUndoable,
  onSend,
  onUndo,
  reduced,
}: Props) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [inputFocused, setInputFocused] = useState(false);
  const scroller = useRef<ScrollView | null>(null);

  const undoneBatches = new Set(
    (history ?? []).filter(r => r.undone_at).map(r => r.batch_id),
  );
  const lastAssistant = [...messages]
    .reverse()
    .find(m => m.role === 'assistant');

  // Keep the newest message in view.
  useEffect(() => {
    scroller.current?.scrollToEnd?.({animated: !reduced});
  }, [messages.length, sending, reduced]);

  const send = (text: string) => {
    const message = text.trim();
    if (!message || sending) return;
    setError(null);
    setDraft('');
    onSend(message, {
      // Nothing typed is lost: the message goes back in the box, unless the
      // reviewer has already started typing something else.
      onError: e => {
        setDraft(current => (current ? current : message));
        setError(chatErrorText(e));
      },
    });
  };

  const length = draft.length;
  const empty = !loading && messages.length === 0 && !sending;

  return (
    <View
      accessibilityLabel="Chat"
      style={{flex: 1, minHeight: 0, backgroundColor: E.surface}}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'baseline',
          gap: S[2],
          paddingHorizontal: S[5],
          paddingTop: S[4],
          paddingBottom: S[3],
          borderBottomWidth: 1,
          borderBottomColor: E.line,
        }}>
        <CCText
          accessibilityRole="header"
          style={{fontSize: T.base, fontWeight: '700', color: E.ink}}>
          Chat
        </CCText>
        <CCText
          style={{
            fontSize: T.sm,
            color: E.muted,
          }}>{`about ${chapterLabel.toLowerCase()}`}</CCText>
      </View>

      <ScrollView
        ref={scroller}
        style={{flex: 1}}
        contentContainerStyle={{padding: S[5], gap: S[5], flexGrow: 1}}>
        {empty ? (
          <View style={{gap: S[4]}}>
            <View style={{gap: S[1]}}>
              <CCText
                style={{
                  fontSize: T.base,
                  fontWeight: '600',
                  color: E.ink,
                  lineHeight: 22,
                }}>
                Tell me what’s wrong with this chapter.
              </CCText>
              <CCText
                style={{fontSize: T.sm + 1, color: E.muted, lineHeight: 20}}>
                Changes apply right away, and every change can be undone.
              </CCText>
            </View>
            <View style={{gap: S[2]}}>
              {suggestions.map(s => (
                <Suggestion key={s} text={s} onPress={() => send(s)} />
              ))}
            </View>
          </View>
        ) : null}

        {messages.map(m =>
          m.role === 'user' ? (
            <View
              key={String(m.id)}
              style={{
                alignSelf: 'flex-end',
                maxWidth: '86%',
                gap: 2,
                alignItems: 'flex-end',
              }}>
              <View
                style={{
                  backgroundColor: E.userBubble,
                  borderRadius: 16,
                  borderBottomRightRadius: 4,
                  paddingHorizontal: S[3] + 2,
                  paddingVertical: S[2] + 1,
                }}>
                <CCText
                  selectable
                  style={{fontSize: T.base, color: E.ink, lineHeight: 22}}>
                  {m.text}
                </CCText>
              </View>
            </View>
          ) : (
            <AssistantMessage
              key={String(m.id)}
              m={m}
              undoable={!!m.batch_id && m.batch_id === latestUndoable}
              undone={!!m.batch_id && undoneBatches.has(m.batch_id)}
              onUndo={() => m.batch_id && onUndo(m.batch_id, m.labels)}
            />
          ),
        )}

        {sending ? <Thinking reduced={reduced} /> : null}
      </ScrollView>

      {/* New replies, announced without interrupting. */}
      <View accessibilityLiveRegion="polite" style={visuallyHidden}>
        {lastAssistant ? <CCText>{lastAssistant.text}</CCText> : null}
      </View>

      <View
        style={{
          paddingHorizontal: S[4],
          paddingTop: S[3],
          paddingBottom: S[4],
          gap: S[2],
          borderTopWidth: 1,
          borderTopColor: E.line,
        }}>
        {error ? <Notice tone="warn" text={error} /> : null}
        <View
          style={[
            {
              flexDirection: 'row',
              alignItems: 'flex-end',
              gap: S[1],
              borderWidth: 1,
              borderColor: inputFocused ? E.accent : E.inputLine,
              borderRadius: 14,
              paddingLeft: S[3],
              paddingRight: 2,
              paddingVertical: 2,
              backgroundColor: E.surface,
            },
          ]}>
          <TextInput
            value={draft}
            onChangeText={t => {
              setDraft(t);
              if (error) setError(null);
            }}
            accessibilityLabel="Message"
            accessibilityHint="Enter sends. Shift and Enter adds a new line."
            placeholder="Say what’s wrong, or ask why…"
            placeholderTextColor={E.muted}
            multiline
            maxLength={MESSAGE_LIMIT}
            blurOnSubmit={false}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            onKeyPress={(e: any) => {
              if (shouldSendOnKey(e?.nativeEvent ?? {})) {
                e.preventDefault?.();
                send(draft);
              }
            }}
            // The browser's own focus outline is replaced by the box's accent
            // border (above), so the whole field reads as focused.
            style={[
              {
                flex: 1,
                minHeight: 40,
                maxHeight: 160,
                paddingVertical: 10,
                fontSize: T.base,
                lineHeight: 21,
                color: E.ink,
              },
              Platform.OS === 'web' ? ({outlineStyle: 'none'} as any) : null,
            ]}
          />
          <IconButton
            icon="arrow-up-circle"
            label="Send"
            color={draft.trim() && !sending ? E.accent : E.muted}
            disabled={!draft.trim() || sending}
            onPress={() => send(draft)}
          />
        </View>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: S[2],
            minHeight: 16,
          }}>
          <CCText style={{fontSize: T.xs, color: E.muted}}>
            Enter to send · Shift+Enter for a new line
          </CCText>
          {length >= MESSAGE_COUNTER_FROM ? (
            <CCText
              accessibilityLiveRegion="polite"
              style={{
                fontSize: T.xs,
                fontWeight: '600',
                color: length >= MESSAGE_LIMIT ? STATUS.warn.fg : E.muted,
                fontVariant: ['tabular-nums'],
              }}>
              {`${length}/${MESSAGE_LIMIT}`}
            </CCText>
          ) : null}
        </View>
      </View>
    </View>
  );
}
